import { Collapse, Icon, InputAdornment, Typography } from '@material-ui/core'
import withStyles from '@material-ui/core/styles/withStyles'
import AddAlert from '@material-ui/icons/AddAlert'
import Email from '@material-ui/icons/Email'
import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import loginPageStyle from '../assets/jss/material-dashboard-pro-react/views/loginPageStyle.jsx'
import LegalInfo from '../components-soapy/LegalInfo.jsx'
import LoadingButton from '../components-soapy/LoadingButton'
import Modal from '../components-soapy/Modal.jsx'
import LanguageSelector from '../components-soapy/MultiLanguage/LanguageSelector'
import Card from '../components/Card/Card.jsx'
import CardBody from '../components/Card/CardBody.jsx'
import CardFooter from '../components/Card/CardFooter.jsx'
import CardHeader from '../components/Card/CardHeader.jsx'
import CookieConsentModal from '../components/Consent/CookieConsentModal.jsx'
import CustomInput from '../components/CustomInput/CustomInput.jsx'
import GridContainer from '../components/Grid/GridContainer.jsx'
import GridItem from '../components/Grid/GridItem.jsx'
import Snackbar from '../components/Snackbar/Snackbar.jsx'
import withAuthentication from '../containers/Authentication/withAuthentication.js'
import withErrorNotification from '../containers/withErrorNotification.js'
import loginRequest from '../endpoint-requests/dashboard-user/login'
import { forgotPassword } from '../endpoint-requests/login/forgotPassword'
import getStatus from '../endpoint-requests/status/getStatus.js'
import removeAllCookies from '../shared/cookie/remove-all-cookies.js'
import useFormFields from '../shared/hooks/useFormFields.js'
import validateFormFields from '../utils/validateFormFields.js'
import { isEmail, verifyLength } from '../utils/validators'
import isValidPassword from '../shared/dashboard-user/isValidPassword.js'
import changeDashboardUserPassword from '../endpoint-requests/dashboard-user/changeDashboardUserPassword'
import { getUser } from '../redux/apiCalls'
import { connect, useDispatch, useSelector } from 'react-redux'
import { onClear } from '../redux/actions'

const EMAIL = 'email'
const PASSWORD = 'password'
const CONFIRM_PASSWORD_1 = 'confirmPassword1'
const CONFIRM_PASSWORD_2 = 'confirmPassword2'

const VALIDATION_STATE = 'ValidationState'

const Login = ({
  t,
  classes,
  history,
  login,
  i18n,
  languageCodes,
  showErrorNotification,
  onClear
}) => {
  const [showCookieConsent, setShowCookieConsent] = useState(
    !localStorage.getItem('useCookies')
  )
  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false)
  const [cardAnimation, setCardAnimation] = useState('cardHidden')
  const [isLoginDisabled, setIsLoginDisabled] = useState(false)
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [isLogin, setIsLogin] = useState(true)
  const dispatch = useDispatch()
  const previousUser = useSelector((state) => state.user.user)
  const [
    isForgotPasswordNotificationActive,
    setIsForgotPasswordNotificationActive,
  ] = useState(false)
  const [isConfirmNewPassword, setIsConfirmNewPassword] = useState(false)
  const [dashboardUser, setDashboardUser] = useState(null)
  const [isEmailNotFoundText, setIsEmailNotFoundText] = useState(false)

  const { formFields, setFormFields } = useFormFields({
    [EMAIL]: {
      name: 'Email',
      isRequired: true,
      value: '',
      validators: [
        {
          isValid: (value) => isEmail(value),
          message: 'Should be in the form of: john@example.com',
        },
      ],
    },
    [PASSWORD]: {
      name: 'Password',
      isRequired: true,
      value: '',
      validators: [
        {
          isValid: (value) => verifyLength(value, 1, 256),
          message: 'Should not be empty, and up to 256 characters',
        },
      ],
    },
    [CONFIRM_PASSWORD_1]: {
      name: 'Password',
      value: '',
      validators: [
        {
          isValid: (value) => verifyLength(value, 1, 256),
          message: 'Should not be empty, and up to 256 characters',
        },
      ],
    },
    [CONFIRM_PASSWORD_2]: {
      name: 'Password',
      value: '',
      validators: [
        {
          isValid: (value) => verifyLength(value, 1, 256),
          message: 'Should not be empty, and up to 256 characters',
        },
      ],
    },
  })

  const handleTextChange = (event, fieldName) => {
    setFormFields({
      ...formFields,
      [fieldName]: { ...formFields[fieldName], value: event.target.value },
      [fieldName + VALIDATION_STATE]: '',
    })
  }

  const changeValidationStateToError = (affectedFieldsNames) => {
    const errorStates = {}
    affectedFieldsNames.forEach((fieldName) => {
      errorStates[fieldName + VALIDATION_STATE] = 'error'
    })

    setFormFields({ ...formFields, ...errorStates })
  }

  const startStatusCheckInterval = () => {
    const statusCheckInterval = setInterval(async () => {
      const isAlive = await getStatus()
      if (isAlive) {
        setIsLoginDisabled(false)
        clearInterval(statusCheckInterval)
      }
    }, 30000)
  }

  const handleCookieConsentApproval = () => {
    localStorage.setItem('cookies_enabled', '1')
    setShowCookieConsent(false)
  }

  const handleCookieConsentMoreInfo = (e) => {
    e.preventDefault()
    setShowCookieConsent(true)
  }

  const submitLoginForm = async (policyAccepted = false) => {
    if (!(localStorage.getItem('cookies_enabled') === '1'))
      return showErrorNotification(
        'You have to accept cookie consent in order to use our service'
      )

    setImmediate(() => setIsAwaitingResponse(true))
    try {
      await getStatus()
    } catch (err) {
      showStatusError()
      setIsAwaitingResponse(false)
      setIsLoginDisabled(true)
      startStatusCheckInterval()
      return
    }
    removeAllCookies()
    try {
      const dashboardUser = await loginRequest({
        email: formFields[EMAIL].value,
        password: formFields[PASSWORD].value,
        policyAccepted,
      })
      if (previousUser.id !== dashboardUser.id) {
        onClear()
      }
      await getUser(dispatch, dashboardUser)
      const { hasTempPassword, organizations } = dashboardUser
      if (hasTempPassword) {
        setIsLogin(false)
        setIsConfirmNewPassword(true)
        setDashboardUser(dashboardUser)
      } else {
        localStorage.setItem('organizations', organizations)
        login(dashboardUser)
        const defaultPage = dashboardUser.defaultPage ? dashboardUser.defaultPage : '/dashboard'
        history.push(defaultPage)
      }
    } catch (err) {
      switch (err.errorType) {
        case 'INVALID_CREDENTIALS':
          showErrorNotification('Invalid credentials, please try again')
          changeValidationStateToError(Object.keys(formFields))
          break
        case 'POLICY_NOT_ACCEPTED':
          setIsInfoModalOpen(true)
          break
        default:
          showErrorNotification(err.message)
      }
    }
    setIsAwaitingResponse(false)
  }

  useEffect(() => {
    setTimeout(function () {
      setCardAnimation('')
    }, 700)
  }, [])

  const showForgotPasswordNotification = () => {
    if (!isForgotPasswordNotificationActive) {
      setIsLogin(true)
      setIsForgotPasswordNotificationActive(true)
      setTimeout(function () {
        setIsForgotPasswordNotificationActive(false)
      }, 5000)
    }
  }

  const submitResetForm = async () => {
    setImmediate(() => setIsAwaitingResponse(true))
    try {
      await forgotPassword(formFields[EMAIL].value)
    } catch (err) {
      setIsEmailNotFoundText(true)
      return false
    }
    return true
  }

  const handleLoginBtnClick = async () => {
    const { isValid, error } = await validateFormFields(formFields)
    if (isValid) {
      submitLoginForm()
    } else {
      showErrorNotification(error.message)
      changeValidationStateToError(error.affectedFieldsNames)
    }
  }

  const handleResetPasswordBtnClick = async (dashboardUser) => {
    const { confirmPassword1, confirmPassword2 } = formFields
    const confirmPassword1Value = confirmPassword1.value
    const confirmPassword2Value = confirmPassword2.value

    if (confirmPassword1Value !== confirmPassword2Value) {
      return showErrorNotification('Passwords does not match')
    } else if (!isValidPassword(confirmPassword1Value)) {
      return showErrorNotification({ passwordError: true })
    }
    setIsAwaitingResponse(true)
    await changeDashboardUserPassword(dashboardUser.id, confirmPassword1Value)
    setIsAwaitingResponse(false)
    login(dashboardUser)
    history.push('/dashboard')
  }

  const handleResetBtnClick = async () => {
    setIsEmailNotFoundText(false)
    const { isValid, error } = await validateFormFields(
      _.pick(formFields, EMAIL)
    )
    if (isValid) {
      const emailExists = await submitResetForm()
      setIsAwaitingResponse(false)
      if (emailExists) showForgotPasswordNotification()
    } else {
      showErrorNotification(error.message)
      changeValidationStateToError(error.affectedFieldsNames)
    }
  }

  const showStatusError = () => {
    showErrorNotification(
      <div>
        <Typography style={{ textAlign: 'center', fontSize: '1rem' }}>
          {t('Something is wrong!')}
        </Typography>
        <Typography style={{ fontSize: '0.9rem' }}>
          {t('We are going to fix it up and have things back to normal soon.')}
        </Typography>
      </div>
    )
  }
  return (
    <div className={classes.container}>
      <div style={{ position: 'absolute', right: '1rem', top: '1rem' }}>
        <LanguageSelector
          isDarkBackground
          languageCodes={languageCodes}
          i18n={i18n}
        />
      </div>
      <GridContainer justify="center">
        <GridItem xs={12} sm={6} md={4}>
          <Snackbar
            place="tc"
            color="info"
            icon={AddAlert}
            onClose={() => setIsForgotPasswordNotificationActive(false)}
            message={t('Password Was Sent')}
            open={isForgotPasswordNotificationActive}
            closeNotification={() =>
              setIsForgotPasswordNotificationActive(false)
            }
            close
          />
          <form>
            {isLogin ? (
              <Card login className={classes[cardAnimation]}>
                <CardHeader
                  style={{
                    backgroundColor: '#00acc1',
                    borderRadius: '6px',
                  }}
                >
                  <img
                    style={{ width: '85%', margin: '0 7.5%' }}
                    src={require('../assets/img/login-logo.png')}
                    alt="logo"
                  />
                </CardHeader>
                <CardBody>
                  <CustomInput
                    error={formFields[EMAIL + VALIDATION_STATE] === 'error'}
                    labelText={`${t('Email')} *`}
                    id="email"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: formFields[EMAIL].value,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Email className={classes.inputAdornmentIcon} />
                        </InputAdornment>
                      ),
                      autoComplete: 'email',
                      onChange: (event) => handleTextChange(event, EMAIL),
                      type: 'email',
                    }}
                  />
                  <CustomInput
                    error={formFields[PASSWORD + VALIDATION_STATE] === 'error'}
                    labelText={`${t('Password')} *`}
                    id="password"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: formFields[PASSWORD].value,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Icon className={classes.inputAdornmentIcon}>
                            lock_outline
                          </Icon>
                        </InputAdornment>
                      ),
                      autoComplete: 'password',
                      onChange: (event) => handleTextChange(event, PASSWORD),
                      type: 'password',
                    }}
                  />
                </CardBody>

                <CardFooter
                  className={`${classes.justifyContentCenter} ${classes.cardFooter
                    }`}
                >
                  <LoadingButton
                    color="info"
                    size="sm"
                    onClick={handleLoginBtnClick}
                    disabled={isAwaitingResponse || isLoginDisabled}
                    isLoading={isAwaitingResponse}
                    id="loginButton"
                    block
                    fullWidth
                  >
                    {t('Login')}
                  </LoadingButton>
                  <div className={classes.forgotPasswordLinkDiv}>
                    <span
                      onClick={() => setIsLogin(!isLogin)}
                      href="# "
                      id="forgotPasswordLink"
                      className={classes.forgotPasswordLinkText}
                    >
                      {t('Forgot Password')}
                    </span>
                  </div>
                </CardFooter>
              </Card>
            ) : (
              <div>
                {!isConfirmNewPassword ? (
                  <Card login className={classes[cardAnimation]}>
                    <CardHeader
                      className={`${classes.cardHeader} ${classes.textCenter}`}
                      color="info"
                    >
                      <h2 className={classes.cardTitle}>Soapy</h2>
                    </CardHeader>
                    <CardBody className={classes.forgotPasswordCard}>
                      <p className={classes.forgotPasswordInstruction}>
                        {t(
                          'Enter the email address associated with your account, and we\'ll email you a new password'
                        )}
                      </p>
                      <CustomInput
                        error={formFields[EMAIL + VALIDATION_STATE] === 'error'}
                        labelText={`${t('Email')} *`}
                        id="email"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          value: formFields[EMAIL].value,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Email className={classes.inputAdornmentIcon} />
                            </InputAdornment>
                          ),
                          autoComplete: 'email',
                          onChange: (event) => handleTextChange(event, EMAIL),
                          type: 'email',
                        }}
                      />
                      {isEmailNotFoundText ? (
                        <div style={{ color: 'red' }}>
                          The email address you entered could not be found
                        </div>
                      ) : null}
                    </CardBody>
                    <CardFooter
                      className={`${classes.justifyContentCenter} ${classes.forgotPasswordCardFooter
                        }`}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div
                          onClick={() => setIsLogin(!isLogin)}
                          className={classes.forgotPasswordCardFooterBackDiv}
                          id="backToLogin"
                        >
                          <Icon
                            className={`${classes.inputAdornmentIcon} ${classes.forgotPasswordCardFooterBackIcon
                              }`}
                          >
                            arrow_back_ios
                          </Icon>
                          <p
                            className={classes.forgotPasswordCardFooterBackText}
                          >
                            {t('Back To Login')}
                          </p>
                        </div>
                        <div className={classes.resetButtonDiv}>
                          <LoadingButton
                            color="info"
                            size="sm"
                            onClick={handleResetBtnClick}
                            disabled={isAwaitingResponse}
                            isLoading={isAwaitingResponse}
                            block
                            fullWidth
                          >
                            {t('Reset')}
                          </LoadingButton>
                        </div>
                      </div>
                    </CardFooter>
                  </Card>
                ) : (
                  <Card login className={classes[cardAnimation]}>
                    <CardBody className={classes.forgotPasswordCard}>
                      <p className={classes.forgotPasswordInstruction}>
                        {t('Confirm new password')}
                      </p>
                      <CustomInput
                        error={
                          formFields[PASSWORD + VALIDATION_STATE] === 'error'
                        }
                        labelText={`${t('Password')} *`}
                        id="password"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          value: formFields[CONFIRM_PASSWORD_1].value,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Icon className={classes.inputAdornmentIcon}>
                                lock_outline
                              </Icon>
                            </InputAdornment>
                          ),
                          autoComplete: 'password',
                          onChange: (event) =>
                            handleTextChange(event, CONFIRM_PASSWORD_1),
                          type: 'password',
                        }}
                      />
                      <CustomInput
                        error={
                          formFields[PASSWORD + VALIDATION_STATE] === 'error'
                        }
                        labelText={`${t('Password')} *`}
                        id="password"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          value: formFields[CONFIRM_PASSWORD_2].value,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Icon className={classes.inputAdornmentIcon}>
                                lock_outline
                              </Icon>
                            </InputAdornment>
                          ),
                          autoComplete: 'password',
                          onChange: (event) =>
                            handleTextChange(event, CONFIRM_PASSWORD_2),
                          type: 'password',
                        }}
                      />
                    </CardBody>
                    <CardFooter
                      className={`${classes.justifyContentCenter} ${classes.forgotPasswordCardFooter
                        }`}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div
                          onClick={() => setIsLogin(!isLogin)}
                          className={classes.forgotPasswordCardFooterBackDiv}
                          id="backToLogin"
                        >
                          <Icon
                            className={`${classes.inputAdornmentIcon} ${classes.forgotPasswordCardFooterBackIcon
                              }`}
                          >
                            arrow_back_ios
                          </Icon>
                          <p
                            className={classes.forgotPasswordCardFooterBackText}
                          >
                            {t('Back To Login')}
                          </p>
                        </div>
                        <div className={classes.resetButtonDiv}>
                          <LoadingButton
                            color="info"
                            size="sm"
                            onClick={() =>
                              handleResetPasswordBtnClick(dashboardUser)
                            }
                            disabled={isAwaitingResponse}
                            isLoading={isAwaitingResponse}
                            block
                            fullWidth
                          >
                            {t('Save')}
                          </LoadingButton>
                        </div>
                      </div>
                    </CardFooter>
                  </Card>
                )}
              </div>
            )}
          </form>
        </GridItem>
      </GridContainer>
      <Modal
        isOpen={isInfoModalOpen}
        applyButtonText={t('Accept')}
        cancelButtonText={t('Reject')}
        title={t('Legal Info')}
        onApply={() => {
          setIsInfoModalOpen(true)
          submitLoginForm(true)
        }}
        onCancel={() => setIsInfoModalOpen(false)}
      >
        <LegalInfo />
      </Modal>
      {!localStorage.getItem('cookies_enabled') ? (
        <>
          {/* <InfoModal
              body=''
              isOpen={this.state.infoModalIsOpen}
              title={t('Legal Info')}
              onClose={() => this.setState({infoModalIsOpen: false})}
            >

            </InfoModal> */}
          <Collapse in={showCookieConsent} timeout="auto" unmountOnExit>
            <CookieConsentModal
              isOpen
              handleApproval={handleCookieConsentApproval}
              handleMoreInfo={handleCookieConsentMoreInfo}
            />
          </Collapse>
        </>
      ) : null}
    </div>
  )
}

export default connect(null, { onClear })(withAuthentication(
  withStyles(loginPageStyle)(withTranslation()(withErrorNotification(Login))))
)
